import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import type { AssistantGlow } from '../assistant/AssistantController';
import type { RoomDto, RoomFloorplanDto } from '../generated/contracts';
import type { RoomHub } from '../net/roomHub';
import type { LockResult } from '../net/wire';
import type { RoomState } from '../room/RoomState';
import { buildLabelledPlate, disposePlate } from './LabelledPlate';
import type { Grabber, GrabberHub } from './Grabbers';

/**
 * A permanent in-room debug affordance, on the south wall, visible only to `lars`
 * (plan/open-items.md — added while the owner was away from a computer, headset-only, with
 * no way to trigger these actions by voice or by console).
 *
 * The gate is a convenience, not a security boundary — say so once, here, rather than at
 * every call site: every button below invokes exactly the same authorised hub method any
 * client could call, and the server remains the only real enforcement (CLAUDE.md, "REST is
 * read-only; every mutation goes through the SignalR hub" and "Grab = lock = permission to
 * edit"). Hiding the panel from everyone else only stops it from being a room fixture other
 * people have to understand, not from being callable.
 *
 * South wall, not north: the whiteboard already owns the north wall (`SceneRoot.buildRoom`,
 * `WhiteboardLayout`), and `SceneRoot.spawnPoint` — "just inside the south wall, looking
 * across the room" — sits at negative Z, confirming `wall-south` (`SceneRoot.ts`) is the
 * -Z wall by construction, not by inference from the name alone.
 *
 * A lifetime object, constructed once in `main.ts` alongside `AuthoringController`, *not*
 * pushed onto `SceneRoot`'s own `disposables` — the room's walls are rebuilt on every
 * `buildRoom()` (a resync, or this panel's own trim-colour action), and re-parenting a debug
 * affordance to those rebuilds for no reason would tear it down and rebuild it on every
 * accent change. Its own meshes are parented to a `TransformNode` this class owns outright,
 * repositioned by `reposition()` whenever the room's floorplan is first known or could have
 * changed (mirroring `WhiteboardLayout.computeWhiteboardLayout`'s own "pure numbers in, a
 * description of where things go out" split from the `Scene`/`Mesh` construction that reads
 * them).
 *
 * Interaction is far-trigger, aimed — the same button `ConnectionDrawer` reads for "point at
 * a node and pull the trigger" — not the tray's dwell, which needs a hand near a slot. A
 * fixed wall panel is reached by pointing at it from across the room on a controller or the
 * desktop pointer alike, so aiming (`aimOrigin` + `forward`) is the only mechanism that works
 * for both without inventing a second gesture. The nearest-along-the-ray test below is the
 * same shape as `ConnectionDrawer.nodeUnderAim`, simplified: these plates are small and fixed,
 * so a flat tolerance radius replaces a bounding-sphere lookup.
 *
 * Reset shapes and reset whiteboard are destructive with no undo (CLAUDE.md, "There is no
 * undo stack in v1"), so both require a deliberate hold — aim and keep the far button pressed
 * for `HOLD_MS` — rather than firing on the first press, the same trade the tray's own delete
 * dwell makes (CLAUDE.md, "Delete waits longer. There is no undo in v1, so a mis-swipe must
 * not be able to destroy someone else's afternoon"). Trim colour, reset-assistant-history and
 * cycle-glow are not destructive in the same sense — a colour, a conversation history, or a
 * light state can all be set again — and fire on a plain press.
 *
 * The `cycle-glow` button steps `AssistantGlow` through `'none' -> 'listening' -> 'speaking' ->
 * 'none'` on every plain press, so the room's assistant-state visuals (the ceiling light and
 * the sky stars, both driven by `SceneRoot.applyAssistantGlow`) can be evaluated without holding
 * a real conversation with Gemma — added while the owner was headset-only with no practical way
 * to trigger a real turn by voice. It is a **local, client-only override**: it never calls the
 * hub, never broadcasts, and has no effect on any other peer's own client — every other browser
 * in the room keeps showing whatever its own `AssistantController` state says, untouched.
 * `'speaking'` needs a moving amplitude to look like anything (`SceneRoot.setAssistantAmplitude`
 * otherwise sits at 0 or a stale sample), so this class drives an obvious, self-contained
 * synthetic oscillation into it for as long as the debug state stays `'speaking'` — see
 * `startSyntheticAmplitude`.
 *
 * A real assistant turn must always win over this override. `SceneRoot.setAssistantGlow` has no
 * concept of "who asked" — it simply repaints the light/stars for whatever glow it is last told
 * — so the moment a genuine `assistantTurnStarted`/reply/`assistantTurnFailed` reaches
 * `AssistantController.onGlowChanged`, the real state already wins outright, for free, by being
 * applied after whatever this panel last set. What that does not do on its own is stop this
 * panel's *synthetic amplitude timer*, which would otherwise keep firing and periodically
 * clobbering the real, audio-driven amplitude with a fake one. `main.ts` calls
 * `debugPanel.yieldToRealGlow()` from inside `assistant.onGlowChanged`, right after applying the
 * real state, specifically to stop that timer and reset this panel's own cycle position back to
 * `'none'` — so the debug override never fights a real turn, and the next press starts a fresh
 * cycle rather than resuming mid-way through a state nobody chose.
 */

/** Height of the panel's own centre above the floor, in metres. Roughly eye level for a
 *  standing adult, and comfortably below `wallHeight` for the seeded 8x8m room. */
const PANEL_HEIGHT_METRES = 1.5;

/** Matches `SceneRoot`'s own wall thickness — see `WhiteboardLayout.ts`'s identical constant
 *  and its own comment; that file cannot import `SceneRoot`'s private number either, so this
 *  is the same "restated, not shared" choice, not an oversight. */
const WALL_THICKNESS = 0.1;

/** Gap kept between the wall's own interior face and the panel, so nothing z-fights —
 *  matches `WhiteboardLayout.ts`'s identical constant and reasoning. */
const WALL_CLEARANCE = 0.01;

/**
 * How far west of room-centre the panel sits on the south wall, so it does not straddle
 * `SceneRoot.spawnPoint` (room-centre, `x = 0`) or a doorway marker that might be centred
 * there. Clamped against the room's own half-width in `reposition()` so a narrower template
 * than the seeded 8x8m default cannot push the panel off its own wall.
 */
const PANEL_X_OFFSET_METRES = 1.6;

/** Clear of the wall on either side, matching `WhiteboardLayout`'s own `BOARD_SIDE_MARGIN`
 *  intent — the panel's own half-width plus this must fit inside the room's half-width. */
const PANEL_SIDE_MARGIN = 0.3;

const TITLE_WIDTH = 0.34;
const TITLE_HEIGHT = 0.06;

const BUTTON_WIDTH = 0.3;
const BUTTON_HEIGHT = 0.075;

const SWATCH_WIDTH = 0.08;
const SWATCH_HEIGHT = 0.075;
const SWATCH_SPACING = 0.09;

/** Vertical gap between row centres, top to bottom. */
const ROW_SPACING = 0.1;

/** Row y-offsets from the panel's own centre, top to bottom — title, then one row per group
 *  in the order plan/open-items.md asked for: reset shapes, reset whiteboard, trim colour,
 *  reset assistant history, then glow cycle.
 *
 *  Uniformly one `ROW_SPACING` apart, deliberately. The glow-cycle row originally sat *four*
 *  spacings down, as a workaround: at a single 0.1m gap — exactly `AIM_TOLERANCE` — the row
 *  above also fell inside tolerance, and `buttonUnderAim` then broke the tie by smallest
 *  distance *along* the ray. Every button on this panel sits on the same wall at nearly the
 *  same `along`, so that tie-break was effectively insertion order, and the newest row was
 *  unreachable even when aimed at dead centre.
 *
 *  Widening the gap hid that. Ranking by `missBy` instead (see `buttonUnderAim`) fixes the
 *  cause, so the spacing no longer has to compensate for it. Keeping the rows uniform is what
 *  keeps the real fix honest: if that tie-break ever regresses, a button goes unreachable
 *  again and the aim tests fail, rather than the geometry quietly papering over it. */
const TITLE_Y = ROW_SPACING * 2;
const RESET_SHAPES_Y = ROW_SPACING;
const RESET_WHITEBOARD_Y = 0;
const EXPORT_WHITEBOARD_Y = -ROW_SPACING;
const EXPORT_MERMAID_Y = -ROW_SPACING * 2;
const TRIM_ROW_Y = -ROW_SPACING * 3;
const RESET_HISTORY_Y = -ROW_SPACING * 4;
const GLOW_CYCLE_Y = -ROW_SPACING * 5;
const GRID_TOGGLE_Y = -ROW_SPACING * 6;

/** The trim swatches' own colours — reused from the tray's existing action/preview palette
 *  (`PaletteUI.ts`'s `NODE_ACTIONS`/`EDGE_ACTIONS`, `ConnectionDrawer.ts`'s `PREVIEW_COLOR`)
 *  rather than inventing a fifth palette, so a trimmed room's accent always lands on a colour
 *  already familiar from elsewhere in this client. */
const TRIM_COLORS = ['#4C8DFF', '#3ED598', '#FF5D8F', '#FFD166', '#C77DFF'];

/** Nearest-along-the-ray test tolerance, in metres — generous enough that a controller's
 *  trigger-pull does not need to be pixel-precise against a 30cm-wide plate from a few
 *  metres away, the same reasoning `ConnectionDrawer.AIM_TOLERANCE` gives for nodes. */
const AIM_TOLERANCE = 0.1;
const MIN_AIM_DISTANCE = 0.15;
const MAX_AIM_DISTANCE = 8;

/** How long the far button must stay held, aimed at the same destructive button, before it
 *  fires — the aimed-and-held counterpart of the tray's own `DELETE_HOLD_MS`/`DELETE_DWELL_MS`
 *  (CLAUDE.md, "Delete waits longer"). Long enough that passing the aim across the button
 *  while reaching for something else cannot fire it by accident. */
const HOLD_MS = 700;

/** Grows as a hold fills, the same "show the delay running" reasoning `PaletteUI.checkForDwell`
 *  gives for its own scale-up — a hold indistinguishable from an inert button is a hold
 *  nobody trusts. */
const HOLD_MAX_SCALE_BUMP = 0.35;

type DebugButtonKind =
  | 'reset-shapes'
  | 'reset-whiteboard'
  | 'export-whiteboard'
  | 'export-mermaid'
  | 'reset-assistant-history'
  | 'trim'
  | 'cycle-glow'
  | 'toggle-grid';

/** The cycle order `cycle-glow` steps through on every press — see this file's own module
 *  comment. Read by `cycleDebugGlow` alone; nothing else needs the sequence. */
const GLOW_CYCLE_ORDER: readonly AssistantGlow[] = ['none', 'listening', 'speaking'];

/** How often the synthetic 'speaking' amplitude is resampled, in ms — a `setInterval`, not
 *  `requestAnimationFrame` (CLAUDE.md, "`requestAnimationFrame` is throttled inside an active
 *  WebXR session"): this has to keep visibly varying inside a real headset session, the exact
 *  case rAF silently fails for, the same reason `PlaybackAmplitude`/`VoiceBands` already use a
 *  timer instead. */
const GLOW_AMPLITUDE_SAMPLE_MS = 80;

/** Period of the synthetic 'speaking' amplitude's own oscillation, in ms — arbitrary, chosen
 *  only so the light/stars visibly pulse rather than sit at one flat, misleading level; not
 *  meant to resemble real speech cadence, just to not look like a dead reading. */
const GLOW_AMPLITUDE_PERIOD_MS = 900;

interface DebugButton {
  readonly mesh: Mesh;
  readonly kind: DebugButtonKind;
  /** Present only for `kind === 'trim'`. */
  readonly trimColor?: string;
}

function isDestructive(kind: DebugButtonKind): boolean {
  return kind === 'reset-shapes' || kind === 'reset-whiteboard';
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export class DebugPanel {
  private readonly root: TransformNode;
  private readonly buttons: DebugButton[] = [];

  /** The title plate — the one `buildLabelledPlate` mesh this panel builds that is never a
   *  `DebugButton`, so it needs its own field for `dispose()` to reach it through
   *  `disposePlate` rather than `root.dispose()`'s own cascade, which frees every descendant
   *  mesh but not their materials or `DynamicTexture`s (CLAUDE.md, "That same cascade does not
   *  free materials"). Null only before `buildContents` has run. */
  private title: Mesh | null = null;

  /** The aimed-and-held destructive button currently filling, or null. Tracks which grabber
   *  owns the hold — the same "one owner at a time" shape `PaletteUI.WheelDrag` uses for a
   *  hand's pinch-drag — so a second grabber aiming at the same button mid-hold cannot reset
   *  or steal someone else's progress. */
  private holdFill: { button: DebugButton; grabberId: string; since: number } | null = null;

  /** Set once, by `reposition()`, when the room's own floorplan is first known. Buttons exist
   *  (built in the constructor) before that, disabled, at the origin — `reposition` is what
   *  actually places and shows them. */
  private positioned = false;

  /** The `cycle-glow` button's own current position in `GLOW_CYCLE_ORDER` — this panel's local
   *  override, not a mirror of `SceneRoot.assistantGlow` (which also reflects a real turn).
   *  Reset to `'none'` by `yieldToRealGlow` whenever a real turn takes over, so the next press
   *  restarts the cycle rather than resuming mid-way through a state this panel never actually
   *  set. */
  private debugGlow: AssistantGlow = 'none';

  /** The `cycle-glow` button's own mesh, rebuilt in place (`updateGlowButtonLabel`) every time
   *  `debugGlow` changes so its label stays legible at a glance — there is no way to repaint a
   *  `buildLabelledPlate` texture in place without reaching into `PaletteUI`'s own private
   *  canvas helpers, so this rebuilds the plate instead, the same "not the everyday case, but
   *  cheap enough on a plain press" trade the trim swatches already make by being built fresh
   *  per room. Null only before `buildContents` has run (a disabled panel). */
  private glowButton: DebugButton | null = null;

  /** The synthetic 'speaking' amplitude's own timer, or null when not running — running only
   *  while `debugGlow === 'speaking'`. See `startSyntheticAmplitude`/`stopSyntheticAmplitude`. */
  private glowAmplitudeTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Whether the finer, `CELL_SIZE`-spaced grid overlay (plan/grid.md) is currently on. The
   * owner deliberately left "does snapping on release need a visible grid to read as
   * intentional rather than as a bug" undecided — this button exists so both can be compared
   * in one headset session rather than the answer being picked in code. Off by default, same
   * as `SceneRoot.cellGridVisible` itself.
   */
  private gridVisible = false;

  /** The `toggle-grid` button's own mesh, rebuilt in place on every toggle — same reasoning as
   *  `glowButton`'s own doc comment: legible feedback at a glance beats a static label. */
  private gridToggleButton: DebugButton | null = null;

  /** A transient message, for the same "refusal or confirmation that does not deserve a
   *  dialog" cases `main.ts`'s own `showNotice` exists for — wired to it by the caller. */
  onNotice: ((message: string) => void) | null = null;

  /**
   * Fired after a trim-colour button's `UpdateRoomAccent` call resolves. `RoomHub.
   * updateRoomAccent`'s own doc comment: this does not also arrive as `roomUpdated` for the
   * caller (CLAUDE.md, "A hub method that returns state does not broadcast it back to you"),
   * so applying it is the caller's job — wired in `main.ts` to `sceneRoot.buildRoom(room,
   * snapshot.template)`, the same re-tint path `roomUpdated` itself already drives for every
   * other peer.
   */
  onAccentChanged: ((room: RoomDto) => void) | null = null;

  /**
   * Fired every time `cycle-glow` advances — wired in `main.ts` to
   * `sceneRoot.setAssistantGlow(glow)`, the exact same call `AssistantController.onGlowChanged`
   * already makes for a real turn. `SceneRoot.setAssistantGlow` does not know or care which
   * caller it came from, which is precisely what lets a real turn simply overwrite this
   * override the instant one starts — see this file's own module comment.
   */
  onDebugGlowChanged: ((glow: AssistantGlow) => void) | null = null;

  /**
   * Fired repeatedly, on a timer, only while the debug state is `'speaking'` — wired in
   * `main.ts` to `sceneRoot.setAssistantAmplitude(level)`, the same call
   * `AssistantController.onAmplitudeChanged` makes for real playback. See
   * `startSyntheticAmplitude`.
   */
  onDebugAmplitude: ((level: number) => void) | null = null;

  /**
   * Fired every time `toggle-grid` flips — wired in `main.ts` to
   * `sceneRoot.setCellGridVisible(visible)`. See `gridVisible`'s own doc comment.
   */
  onGridVisibleChanged: ((visible: boolean) => void) | null = null;

  /**
   * @param enabled Whether this browser's signed-in user is `lars` — read once, by the
   *   caller, off `AuthUserDto.username` (`main.ts`'s own `account`), not re-checked here.
   *   A convenience gate, not a security boundary — see this class's own doc comment. When
   *   false, no meshes are ever built at all, not merely hidden: `buttonCount` stays 0 and
   *   `reposition()` is a no-op, so there is nothing for a non-`lars` session to accidentally
   *   render or aim at.
   */
  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly hub: RoomHub,
    private readonly state: RoomState,
    private readonly enabled: boolean,
  ) {
    this.root = new TransformNode('debug-panel', scene);
    this.root.setEnabled(false);

    if (!this.enabled) {
      return;
    }

    this.buildContents();
  }

  /** How many interactive buttons exist — 0 for a disabled (non-`lars`) panel, otherwise
   *  4 fixed actions (reset-shapes, reset-whiteboard, export-whiteboard, export-mermaid) +
   *  one per trim colour + 3 more fixed actions (reset-assistant-history, cycle-glow,
   *  toggle-grid). Exposed for tests, the same "expose enough to confirm state, not
   *  internals" reasoning `PaletteUI.activeCategory` documents for itself. */
  get buttonCount(): number {
    return this.buttons.length;
  }

  /** Whether the panel is currently positioned and shown — false before the first
   *  `reposition()` call, and always false for a disabled panel. */
  get isVisible(): boolean {
    return this.enabled && this.positioned;
  }

  /**
   * Places the panel on the south wall for a room of this shape, and shows it. Safe to call
   * repeatedly — `main.ts` calls this once after the room's first `buildRoom()`, and again
   * after any resync whose snapshot could carry a different template; a same-shaped
   * floorplan simply reproduces the same transform. A no-op for a disabled panel.
   *
   * Geometry mirrors `computeWhiteboardLayout`'s own derivation for the *north* wall, mirrored
   * onto the south one: `wallInnerZ` is the south wall's own interior face (negative Z, so
   * `+WALL_THICKNESS / 2` moves inward from the wall's centreline rather than `-`), and the
   * panel sits `WALL_CLEARANCE` further still into the room, past that face, matching the
   * board's own gap on the opposite wall. The panel is yawed 180 degrees from `buildLabelledPlate`'s
   * own default orientation: a `CreatePlane`'s front face is -Z (CLAUDE.md, "A Babylon plane's
   * front face is -Z"), and this panel's front has to face +Z — toward the room's interior
   * from the south wall, the mirror image of the board's own -Z-facing front from the north
   * wall — so every plate built at local (x, y, 0) needs a root yaw of Math.PI to end up
   * facing the right way once parented under it.
   */
  reposition(floorplan: RoomFloorplanDto): void {
    if (!this.enabled) {
      return;
    }

    const halfWidth = floorplan.width / 2;
    const halfDepth = floorplan.depth / 2;

    const wallInnerZ = -halfDepth + WALL_THICKNESS / 2;
    const mountZ = wallInnerZ + WALL_CLEARANCE;

    // Clamped so a narrower-than-default template cannot push the panel past its own wall's
    // edge, the same guard WhiteboardLayout's own boardWidth clamp exists for.
    const maxOffset = Math.max(0, halfWidth - PANEL_SIDE_MARGIN);
    const x = -clampNumber(PANEL_X_OFFSET_METRES, 0, maxOffset);

    // Kept below wallHeight regardless of how short a template's walls are, so the panel
    // cannot float above them.
    const y = Math.min(PANEL_HEIGHT_METRES, Math.max(0.6, floorplan.wallHeight - 0.6));

    this.root.position.set(x, y, mountZ);
    this.root.rotationQuaternion = Quaternion.FromEulerAngles(0, Math.PI, 0);
    this.root.setEnabled(true);
    this.positioned = true;
  }

  /** Advances one tick's worth of aim/hold state. Registered from `main.ts` the same way
   *  every other per-frame gesture reader is — see `start()`. A no-op for a disabled panel,
   *  so nothing here runs at all for anyone but `lars`. */
  private tick(nowMs: number): void {
    if (!this.enabled || !this.positioned) {
      return;
    }

    let holderStillAiming = false;

    for (const grabber of this.grabbers.grabbers) {
      const button = this.buttonUnderAim(grabber);

      if (button === null) {
        continue;
      }

      if (isDestructive(button.kind)) {
        if (!grabber.far.pressed) {
          continue;
        }

        if (
          this.holdFill !== null &&
          this.holdFill.button === button &&
          this.holdFill.grabberId === grabber.id
        ) {
          holderStillAiming = true;
        } else if (this.holdFill === null) {
          this.holdFill = { button, grabberId: grabber.id, since: nowMs };
          holderStillAiming = true;
        } else {
          // A different grabber (or a different button) already owns the fill in progress —
          // this one does not steal or reset it, matching `PaletteUI.tickWheelHandDrag`'s own
          // "one owner at a time" rule for a pinch-drag.
          continue;
        }

        const progress = Math.min(1, (nowMs - this.holdFill.since) / HOLD_MS);
        button.mesh.scaling.setAll(1 + progress * HOLD_MAX_SCALE_BUMP);

        if (progress >= 1) {
          this.holdFill = null;
          button.mesh.scaling.setAll(1);
          this.fire(button);
        }

        continue;
      }

      if (grabber.far.justPressed) {
        this.fire(button);
      }
    }

    if (!holderStillAiming && this.holdFill !== null) {
      this.holdFill.button.mesh.scaling.setAll(1);
      this.holdFill = null;
    }
  }

  /** Registered the same way every other per-frame gesture reader in this project is
   *  (`ConnectionDrawer.start`, `PaletteUI.start`) — returns the teardown. */
  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick(performance.now()));
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  /**
   * Nearest button along the grabber's aim ray, or null — the same shape as
   * `ConnectionDrawer.nodeUnderAim`, simplified: these plates are small, fixed and never move,
   * so a flat `AIM_TOLERANCE` radius around each plate's own position replaces a bounding-
   * sphere lookup.
   */
  private buttonUnderAim(grabber: Grabber): DebugButton | null {
    let best: DebugButton | null = null;
    // Ranked by how close the ray passes to each button, NOT by distance along the ray. Every
    // button on this panel sits on the same wall and therefore at nearly the same `along`, so
    // ranking by `along` ties across the whole panel and resolves to whichever button happened
    // to be built first — which made the last row added unreachable, aiming dead-centre at it
    // returning its neighbour instead. `missBy` is the value that actually distinguishes "the
    // one being pointed at" from "one that merely happens to be nearby", which is what this
    // method claims to answer. `along` is still what bounds the search (below); it is only
    // wrong as the tie-break.
    let bestMissBy = Number.POSITIVE_INFINITY;

    for (const button of this.buttons) {
      button.mesh.computeWorldMatrix(true);
      const position = button.mesh.getAbsolutePosition();

      const toButton = position.subtract(grabber.aimOrigin);
      const along = Vector3.Dot(toButton, grabber.forward);

      if (along < MIN_AIM_DISTANCE || along > MAX_AIM_DISTANCE) {
        continue;
      }

      const closestOnRay = grabber.aimOrigin.add(grabber.forward.scale(along));
      const missBy = Vector3.Distance(closestOnRay, position);

      if (missBy > AIM_TOLERANCE) {
        continue;
      }

      if (missBy < bestMissBy) {
        bestMissBy = missBy;
        best = button;
      }
    }

    return best;
  }

  private fire(button: DebugButton): void {
    switch (button.kind) {
      case 'reset-shapes':
        void this.resetShapes();
        return;
      case 'reset-whiteboard':
        void this.resetWhiteboard();
        return;
      case 'export-whiteboard':
        void this.exportWhiteboard();
        return;
      case 'export-mermaid':
        void this.exportMermaid();
        return;
      case 'reset-assistant-history':
        void this.resetAssistantHistory();
        return;
      case 'trim':
        void this.applyTrim(button.trimColor ?? TRIM_COLORS[0]!);
        return;
      case 'cycle-glow':
        this.cycleDebugGlow();
        return;
      case 'toggle-grid':
        this.toggleGrid();
        return;
    }
  }

  /** Flips `gridVisible`, applies it (`onGridVisibleChanged`), and relabels the button — a
   *  plain press, not a hold: this is a view toggle, not a destructive action, the same
   *  distinction `isDestructive` already draws for every other button on this panel. */
  private toggleGrid(): void {
    this.gridVisible = !this.gridVisible;
    this.onGridVisibleChanged?.(this.gridVisible);
    this.updateGridToggleButtonLabel();
  }

  /**
   * Advances `debugGlow` to the next state in `GLOW_CYCLE_ORDER`, applies it locally (never to
   * the hub — see this file's own module comment), and starts or stops the synthetic amplitude
   * timer to match. Called only from a plain press (`fire`), never from `yieldToRealGlow`.
   */
  private cycleDebugGlow(): void {
    const currentIndex = GLOW_CYCLE_ORDER.indexOf(this.debugGlow);
    const next = GLOW_CYCLE_ORDER[(currentIndex + 1) % GLOW_CYCLE_ORDER.length]!;
    this.applyDebugGlow(next);
  }

  private applyDebugGlow(glow: AssistantGlow): void {
    this.debugGlow = glow;
    this.onDebugGlowChanged?.(glow);
    this.updateGlowButtonLabel();
    this.stopSyntheticAmplitude();

    if (glow === 'speaking') {
      this.startSyntheticAmplitude();
    }
  }

  /**
   * Drives an obvious, self-contained synthetic amplitude into `onDebugAmplitude` while the
   * debug state is `'speaking'` — a plain sine oscillation between 0 and 1, not any attempt at
   * resembling real speech, just enough variation that the state reads as "something is
   * happening" rather than a flat, misleading dead level (see this file's own module comment on
   * why a flat level would be worse than no override at all). A `setInterval`, not
   * `requestAnimationFrame` — see `GLOW_AMPLITUDE_SAMPLE_MS`'s own doc comment.
   */
  private startSyntheticAmplitude(): void {
    const startedAtMs = Date.now();

    this.glowAmplitudeTimer = setInterval(() => {
      const elapsedMs = Date.now() - startedAtMs;
      const level = 0.5 + 0.5 * Math.sin((elapsedMs / GLOW_AMPLITUDE_PERIOD_MS) * Math.PI * 2);
      this.onDebugAmplitude?.(level);
    }, GLOW_AMPLITUDE_SAMPLE_MS);
  }

  private stopSyntheticAmplitude(): void {
    if (this.glowAmplitudeTimer !== null) {
      clearInterval(this.glowAmplitudeTimer);
      this.glowAmplitudeTimer = null;
    }
  }

  /**
   * Called by `main.ts` from inside `assistant.onGlowChanged`, right after a real turn's own
   * state has already been applied to the scene (`sceneRoot.setAssistantGlow`) — see this
   * file's own module comment for why the real state wins for free and this method only cleans
   * up what would otherwise keep fighting it: the synthetic amplitude timer. Also resets
   * `debugGlow` back to `'none'`, so the next press restarts the cycle from the beginning
   * rather than resuming a state this panel never actually set. A no-op for a disabled panel —
   * `glowButton` is null and there is nothing to relabel.
   */
  yieldToRealGlow(): void {
    this.stopSyntheticAmplitude();

    if (this.debugGlow === 'none') {
      return;
    }

    this.debugGlow = 'none';
    this.updateGlowButtonLabel();
  }

  /**
   * Deletes every node in the room. There is no bulk hub method (CLAUDE.md — see this file's
   * own module comment for the pointer to `RoomSyncHub`), so this follows the exact discipline
   * `tools/scripts/lib/roomClear.mjs` already uses for the same job from a Node script: acquire
   * each node's lock first, and skip — rather than fight over — anything already held by
   * another peer. `DeleteNode` cascades that node's own connections server-side, so nothing
   * here has to touch `state.connections` at all.
   */
  private async resetShapes(): Promise<void> {
    try {
      const nodeIds = [...this.state.nodes.keys()];
      let deleted = 0;
      let skipped = 0;

      for (const nodeId of nodeIds) {
        let grant: LockResult;

        try {
          grant = await this.hub.acquireNodeLock(nodeId);
        } catch {
          // Already gone — a race with someone else's delete between the snapshot above and
          // this call, the same case roomClear.mjs's own nodesSkippedGone covers.
          skipped += 1;
          continue;
        }

        if (!grant.granted) {
          skipped += 1;
          continue;
        }

        try {
          await this.hub.deleteNode(nodeId);
          deleted += 1;
        } catch {
          skipped += 1;
        }
      }

      this.onNotice?.(
        skipped > 0
          ? `reset shapes — ${deleted} removed, ${skipped} left (held by someone else)`
          : `reset shapes — ${deleted} removed`,
      );
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * Deletes every stroke in the room. Unlike nodes, a stroke is never held — `DeleteStroke`
   * needs no lock and a stale id is a genuine server-side no-op (`RoomHub.deleteStroke`'s own
   * doc comment) — so this is a plain sweep with no acquire step, matching roomClear.mjs.
   *
   * Snapshots the board first, automatically (plan/identity-and-portal.md, "Clearing the
   * board snapshots it first, automatically") — this deletes the "did you remember to export
   * before wiping" question rather than answering it, the same reasoning
   * `AssistantRoomClient.ClearWhiteboardAsync` follows server-side for the assistant's own
   * `clear_whiteboard` tool. `exportWhiteboard` resolves to `null` for an already-empty
   * board, which is exactly the case where there is nothing to snapshot.
   */
  // spec: SPEC-100
  private async resetWhiteboard(): Promise<void> {
    try {
      const exported = await this.hub.exportWhiteboard();

      const strokeIds = [...this.state.strokes.keys()];
      let deleted = 0;

      for (const strokeId of strokeIds) {
        await this.hub.deleteStroke(strokeId);
        deleted += 1;
      }

      this.onNotice?.(
        exported
          ? `reset whiteboard — ${deleted} strokes removed, exported first`
          : `reset whiteboard — ${deleted} strokes removed`,
      );
    } catch (error) {
      // Worst case in this class: a `deleteStroke` throwing part-way through the loop used to
      // abort silently, having deleted some strokes already, with no notice at all — matching
      // `resetAssistantHistory`/`applyTrim`'s own shape closes that gap, though it cannot say
      // exactly how many strokes were removed before the failure the way the success path
      // does, since the count that mattered was captured inside the `try`.
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * Explicit export, with no clear — the trigger `plan/identity-and-portal.md` asks for
   * beyond the automatic one on clear. Lives here, as a plain non-destructive button, rather
   * than as an assistant tool: exporting is a mechanical action with nothing for Gemma to
   * decide ("clear the board" already snapshots on its own, and "export the board" has no
   * natural follow-up conversation the way asking her to change a room's colour does), and
   * this panel is already where every other "no live client UI yet" action added by this
   * milestone lives.
   */
  private async exportWhiteboard(): Promise<void> {
    try {
      const exported = await this.hub.exportWhiteboard();

      this.onNotice?.(exported ? 'whiteboard exported' : 'nothing to export — board is empty');
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * The diagram's own explicit export, alongside the whiteboard's — a plain non-destructive
   * button, the same reasoning `exportWhiteboard`'s own doc comment gives: mechanical, with
   * nothing for Gemma to decide, so it lives here rather than as an assistant tool.
   * `RoomSyncHub.ExportMermaid` resolves to `null` for a room with no nodes and no
   * connections, which is exactly the case where there is nothing to snapshot.
   */
  private async exportMermaid(): Promise<void> {
    try {
      const exported = await this.hub.exportMermaid();

      this.onNotice?.(exported ? 'diagram exported as mermaid' : 'nothing to export — room is empty');
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  private async resetAssistantHistory(): Promise<void> {
    try {
      await this.hub.clearAssistantHistory();
      this.onNotice?.('assistant history cleared');
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  private async applyTrim(color: string): Promise<void> {
    try {
      const room = await this.hub.updateRoomAccent(color);

      // Not also a `roomUpdated` broadcast for this caller — see this field's own doc
      // comment on `onAccentChanged`.
      this.onAccentChanged?.(room);
      this.onNotice?.('accent updated');
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  private buildContents(): void {
    const title = buildLabelledPlate(
      this.scene,
      'debug-panel-title',
      'debug (lars)',
      '#12151A',
      '#8A93A6',
      TITLE_WIDTH,
      TITLE_HEIGHT,
    );
    title.position.set(0, TITLE_Y, 0);
    title.parent = this.root;
    title.isPickable = false;
    this.title = title;

    this.addButton('reset-shapes', 'reset shapes', '#FF5D8F', new Vector3(0, RESET_SHAPES_Y, 0));
    this.addButton(
      'reset-whiteboard',
      'reset whiteboard',
      '#FF5D8F',
      new Vector3(0, RESET_WHITEBOARD_Y, 0),
    );
    this.addButton(
      'export-whiteboard',
      'export whiteboard',
      '#4C8DFF',
      new Vector3(0, EXPORT_WHITEBOARD_Y, 0),
    );
    this.addButton(
      'export-mermaid',
      'export mermaid',
      '#4C8DFF',
      new Vector3(0, EXPORT_MERMAID_Y, 0),
    );

    const half = (TRIM_COLORS.length - 1) / 2;

    TRIM_COLORS.forEach((color, index) => {
      const mesh = buildLabelledPlate(
        this.scene,
        `debug-panel-trim-${index}`,
        '',
        color,
        '#12151A',
        SWATCH_WIDTH,
        SWATCH_HEIGHT,
      );
      mesh.position.set((index - half) * SWATCH_SPACING, TRIM_ROW_Y, 0);
      mesh.parent = this.root;
      mesh.isPickable = false;

      this.buttons.push({ mesh, kind: 'trim', trimColor: color });
    });

    this.addButton(
      'reset-assistant-history',
      'reset assistant history',
      '#4C8DFF',
      new Vector3(0, RESET_HISTORY_Y, 0),
    );

    this.addGlowButton();
    this.addGridToggleButton();
  }

  /** Label text for the current `debugGlow` — legible at a glance from across the room, the
   *  point of this button existing at all (this file's own module comment: "the owner will be
   *  pressing it in a headset, alone, with no console"). */
  private glowButtonLabel(): string {
    return `assistant glow: ${this.debugGlow}`;
  }

  private addGlowButton(): void {
    const mesh = buildLabelledPlate(
      this.scene,
      'debug-panel-cycle-glow',
      this.glowButtonLabel(),
      '#4C8DFF',
      '#12151A',
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
    );
    mesh.position.set(0, GLOW_CYCLE_Y, 0);
    mesh.parent = this.root;
    mesh.isPickable = false;

    const button: DebugButton = { mesh, kind: 'cycle-glow' };
    this.buttons.push(button);
    this.glowButton = button;
  }

  /** Rebuilds the `cycle-glow` plate with a fresh label after `debugGlow` changes — see
   *  `glowButton`'s own doc comment for why a rebuild, not an in-place repaint. Disposes the
   *  old mesh and material *before* creating the new one (rather than after) so the two never
   *  briefly coexist under the same mesh name, which `scene.getMeshByName` — used by this
   *  file's own tests, the same way `DebugPanel.test.ts` already reads other buttons — would
   *  otherwise resolve ambiguously between. */
  private updateGlowButtonLabel(): void {
    const previous = this.glowButton;

    if (previous === null) {
      return;
    }

    const index = this.buttons.indexOf(previous);
    const material = previous.mesh.material;

    if (material instanceof StandardMaterial) {
      material.diffuseTexture?.dispose();
    }

    material?.dispose();
    previous.mesh.dispose();

    const mesh = buildLabelledPlate(
      this.scene,
      'debug-panel-cycle-glow',
      this.glowButtonLabel(),
      '#4C8DFF',
      '#12151A',
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
    );
    mesh.position.set(0, GLOW_CYCLE_Y, 0);
    mesh.parent = this.root;
    mesh.isPickable = false;

    const button: DebugButton = { mesh, kind: 'cycle-glow' };
    this.glowButton = button;

    if (index >= 0) {
      this.buttons[index] = button;
    } else {
      this.buttons.push(button);
    }
  }

  /** Label text for the current `gridVisible` — same "legible at a glance" reasoning as
   *  `glowButtonLabel`. */
  private gridToggleLabel(): string {
    return `grid: ${this.gridVisible ? 'on' : 'off'}`;
  }

  private addGridToggleButton(): void {
    const mesh = buildLabelledPlate(
      this.scene,
      'debug-panel-toggle-grid',
      this.gridToggleLabel(),
      '#4C8DFF',
      '#12151A',
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
    );
    mesh.position.set(0, GRID_TOGGLE_Y, 0);
    mesh.parent = this.root;
    mesh.isPickable = false;

    const button: DebugButton = { mesh, kind: 'toggle-grid' };
    this.buttons.push(button);
    this.gridToggleButton = button;
  }

  /** Rebuilds the `toggle-grid` plate with a fresh label after `gridVisible` changes — same
   *  dispose-then-rebuild reasoning as `updateGlowButtonLabel`. */
  private updateGridToggleButtonLabel(): void {
    const previous = this.gridToggleButton;

    if (previous === null) {
      return;
    }

    const index = this.buttons.indexOf(previous);
    const material = previous.mesh.material;

    if (material instanceof StandardMaterial) {
      material.diffuseTexture?.dispose();
    }

    material?.dispose();
    previous.mesh.dispose();

    const mesh = buildLabelledPlate(
      this.scene,
      'debug-panel-toggle-grid',
      this.gridToggleLabel(),
      '#4C8DFF',
      '#12151A',
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
    );
    mesh.position.set(0, GRID_TOGGLE_Y, 0);
    mesh.parent = this.root;
    mesh.isPickable = false;

    const button: DebugButton = { mesh, kind: 'toggle-grid' };
    this.gridToggleButton = button;

    if (index >= 0) {
      this.buttons[index] = button;
    } else {
      this.buttons.push(button);
    }
  }

  private addButton(kind: DebugButtonKind, label: string, background: string, at: Vector3): void {
    const mesh = buildLabelledPlate(
      this.scene,
      `debug-panel-${kind}`,
      label,
      background,
      '#12151A',
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
    );
    mesh.position.copyFrom(at);
    mesh.parent = this.root;
    mesh.isPickable = false;

    this.buttons.push({ mesh, kind });
  }

  /**
   * `root.dispose()` alone used to be the whole body here — it cascades into every descendant
   * mesh (`Node.dispose`'s own default `doNotRecurse: false`), so nothing under it ever
   * leaked as a *mesh*. But that cascade's second argument, `disposeMaterialAndTextures`, also
   * defaults to `false` and is never told otherwise, so it frees the meshes and leaves each
   * one's `StandardMaterial` and its `DynamicTexture` alive, un-disposed, and unreachable
   * (CLAUDE.md, "That same cascade does not free materials" — found proving M13's tray-mini
   * leak test red by hand, the identical mechanism). Roughly 13 plates (the title plus every
   * `DebugButton`) each build one of each, so a panel opened and rebuilt repeatedly leaked one
   * material and one texture per plate, per rebuild. Disposing each plate through
   * `disposePlate` first — the same helper every other builder of one of these plates already
   * routes through, `LabelledPlate.disposePlate`'s own doc names exactly this cascade as the
   * reason it exists — frees the material and texture explicitly; `root.dispose()` afterward
   * then has no descendants left to cascade into at all.
   */
  dispose(): void {
    this.stopSyntheticAmplitude();

    if (this.title) {
      disposePlate(this.title);
      this.title = null;
    }

    for (const button of this.buttons.splice(0)) {
      disposePlate(button.mesh);
    }

    this.glowButton = null;
    this.gridToggleButton = null;

    this.root.dispose();
  }
}
